import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProfileSettings } from '@/components/settings/ProfileSettings';
import { UserManagement } from '@/components/settings/UserManagement';
import { OrganizationSettings } from '@/components/settings/OrganizationSettings';
import { AiUsageDashboard } from '@/components/settings/AiUsageDashboard';
import { AiSettings } from '@/components/settings/AiSettings';
import { TagManager } from '@/components/tags';
import { EmailTemplatesManager } from '@/components/email/EmailTemplatesManager';
import { CalendarIntegrationSettings } from '@/components/interviews/CalendarIntegrationSettings';
import { JobBoardSettings } from '@/components/job-boards/JobBoardSettings';
import { NotificationPreferencesSettings } from '@/components/settings/NotificationPreferencesSettings';
import { User, Building2, Users, Bot, BarChart3, Tags, Mail, CalendarDays, Globe, Bell } from 'lucide-react';

export function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
        <p className="text-text-secondary mt-1">Manage your account, organization, and AI settings</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="h-auto min-h-10 flex-wrap">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Profile
          </TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="organization" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Organization
              </TabsTrigger>
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Users
              </TabsTrigger>
            </>
          )}
          <TabsTrigger value="ai-usage" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            AI Usage
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="ai-settings" className="flex items-center gap-2">
              <Bot className="h-4 w-4" />
              AI Config
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="tags" className="flex items-center gap-2">
              <Tags className="h-4 w-4" />
              Tags
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="email-templates" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Templates
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="calendar" className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Calendar
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="job-boards" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Job Boards
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <ProfileSettings />
        </TabsContent>

        {isAdmin && (
          <>
            <TabsContent value="organization" className="mt-6">
              <OrganizationSettings />
            </TabsContent>
            <TabsContent value="users" className="mt-6">
              <UserManagement />
            </TabsContent>
          </>
        )}

        <TabsContent value="ai-usage" className="mt-6">
          <AiUsageDashboard />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="ai-settings" className="mt-6">
            <AiSettings />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="tags" className="mt-6">
            <TagManager />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="email-templates" className="mt-6">
            <EmailTemplatesManager />
          </TabsContent>
        )}

        <TabsContent value="notifications" className="mt-6">
          <NotificationPreferencesSettings />
        </TabsContent>

        <TabsContent value="calendar" className="mt-6">
          <CalendarIntegrationSettings />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="job-boards" className="mt-6">
            <JobBoardSettings />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
